import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { FeatureFlags } from 'src/app/services/configuration.service.model';
import { ICInstrument, ICInstrumentGroup } from 'src/app/services/image-capture/ic.service.model';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import {
  ICSyncDeviceComponent,
  ICSyncDeviceComponentData,
} from '../../sync-device/ic-sync-device.component';

export interface ReplaceInstrumentComponentData {
  instrumentGroups: ICInstrumentGroup[];
  selectedInstruments: ICInstrument[];
  lutFiles: string[];
  currentInstrumentSerialNumber: string;
  lastCassettes?: string[];
}
@Component({
  selector: 'ic-replace-instrument',
  templateUrl: './replace-instrument.component.html',
  styleUrls: ['./replace-instrument.component.scss'],
  standalone: false,
})
export class ReplaceInstrumentComponent extends BaseComponent implements OnInit {
  instrumentGroups: ICInstrumentGroup[] = [];
  selectedInstruments: ICInstrument[] = [];
  lutFiles: string[] = [];
  customSerialNumber: string = '';
  currentInstrumentSerialNumber: string = '';
  featureFlags$: Observable<FeatureFlags | null>;
  lastCassettes: string[] = [];

  constructor(
    private notificationService: NotificationService,
    private matDialogService: MatDialog,
    public dialogRef: MatDialogRef<ReplaceInstrumentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReplaceInstrumentComponentData,
    private dialogService: DialogService,
    private configService: ConfigurationService,
    private apiService: LabpartnerService,
  ) {
    super();
    this.featureFlags$ = this.configService.featureFlags$;
    this.instrumentGroups = data.instrumentGroups;
    this.selectedInstruments = data.selectedInstruments;
    this.lutFiles = data.lutFiles;
    this.currentInstrumentSerialNumber = data.currentInstrumentSerialNumber;
    this.lastCassettes = data.lastCassettes || [];
  }

  ngOnInit(): void { }

  protected ngOnDestroyInternal(): void { }

  onSelectGroup(group: ICInstrumentGroup) {
    this.notificationService.info(`${group.name} Instrument Group Added`);
    group.instruments.forEach((instrument: ICInstrument) => {
      this.onSelectItem(instrument, false);
    });
  }

  onSelectItem(instrument: ICInstrument, showNotification: boolean = true) {
    if (instrument.instrumentSerial === this.currentInstrumentSerialNumber) {
      if (showNotification) {
        this.notificationService.warn(
          `Should select a different instrument to replace ${this.currentInstrumentSerialNumber}`,
        );
      }
      return;
    }
    if (showNotification) {
      this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
    }
    const instrumentIndex = this.selectedInstruments.findIndex(
      i => i.instrumentId == instrument.instrumentId,
    );
    if (instrumentIndex == -1) {
      this.selectedInstruments = [];
      this.selectedInstruments.push(instrument);
    }
  }

  onRemoveSelectedItem(instrument: ICInstrument) {
    this.removeInstrument(instrument);
  }

  syncDevice(instrument: any) {
    this.featureFlags$.subscribe(flags => {
      // Check if the feature flag for device sync is enabled
      if (flags?.EnableImageCaptureRequestDeviceSync) {
        this.subscription.add(
          this.apiService.icSyncDevice(instrument.instrumentSerial).subscribe(
            () => {
              const dialogConfig: MatDialogConfig<ICSyncDeviceComponentData> = {
                disableClose: false,
                autoFocus: true,
                width: '60%',
                data: {
                  instrumentSerial: instrument.instrumentSerial,
                },
              };
              const dialogRef = this.matDialogService.open<
                ICSyncDeviceComponent,
                ICSyncDeviceComponentData,
                string[]
              >(ICSyncDeviceComponent, dialogConfig);
              this.subscription.add(
                dialogRef.afterClosed().subscribe((dialogResult: string[] | undefined) => {
                  if (!dialogResult || dialogResult.length === 0) {
                    return;
                  }
                  this.lutFiles = dialogResult;
                  this.setLUTFiles();
                }),
              );
            },
            () => {
              this.notificationService.error('Error syncing device. Please try again later.');
            },
          ),
        );
      } else {
        const link = `<a target="_blank" href='${this.configService.getConfig().lutFileFormURl}'>form</a>`;
        this.subscription.add(
          this.dialogService
            .openConfirmDialog(
              `The LUT (Look Up Table) file for this instrument does not exist in the cloud. The Assay Tools Team needs to sync them manually for the time being, please fill out this ${link} to request the LUT file upload for this instrument.
            `,
              { isOk: true, isV2: true, title: 'LUT', width: '800px' },
            )
            .afterClosed()
            .subscribe(() => { }),
        );
      }
    });
  }

  setLUTFiles() {
    this.instrumentGroups.forEach((group: ICInstrumentGroup) => {
      group.instruments.forEach((instrument: ICInstrument) => {
        instrument.hasLUTFile = this.lutFiles.includes(instrument.instrumentSerial);
      });
    });
    this.selectedInstruments.forEach((instrument: ICInstrument) => {
      instrument.hasLUTFile = this.lutFiles.includes(instrument.instrumentSerial);
    });
  }

  removeInstrument(instrument: any) {
    this.notificationService.info(`${instrument.instrumentSerial} Instrument Removed`);
    const instrumentIndex = this.selectedInstruments.findIndex(
      i =>
        i.instrumentId == instrument.instrumentId &&
        i.instrumentSerial == instrument.instrumentSerial,
    );
    if (instrumentIndex != -1) {
      this.selectedInstruments.splice(instrumentIndex, 1);
    }
  }

  onAddInstrument() {
    // Check if the custom serial number is empty
    if (this.customSerialNumber == '') {
      this.notificationService.warn('Please enter a serial number');
      return;
    }

    // Check if the instrument already exists in the selected instruments
    const existingInstrument = this.selectedInstruments.find(
      i => i.instrumentSerial === this.customSerialNumber,
    );
    if (existingInstrument) {
      this.notificationService.warn(`${this.customSerialNumber} Instrument Already Added`);
      this.customSerialNumber = ''; // Clear the custom serial number after adding
      return;
    }

    // Check if the custom serial number is already in use
    for (const group of this.instrumentGroups) {
      const foundInstrument = group.instruments.find(
        i => i.instrumentSerial === this.customSerialNumber,
      );
      if (foundInstrument) {
        this.onSelectItem(foundInstrument);
        this.customSerialNumber = ''; // Clear the custom serial number after adding
        return;
      }
    }

    this.apiService.getOrAddInstrumentBySerial(this.customSerialNumber).subscribe(
      (instrument: ICInstrument) => {
        if (instrument.hasLUTFile) {
          if (!this.lutFiles.includes(instrument.instrumentSerial)) {
            this.lutFiles.push(instrument.instrumentSerial);
          }
        }
        this.selectedInstruments = [instrument];
        this.notificationService.info(`${instrument.instrumentSerial} Instrument Added`);
        this.customSerialNumber = '';
      },
      () => {
        this.notificationService.warn(`Instrument does not exist`);
        this.customSerialNumber = '';
      },
    );

  }

  cancel() {
    this.dialogRef.close(null);
  }

  // Confirm the replacement of the instrument
  continue() {
    this.subscription.add(
      this.apiService
        .checkICInstrumentAvailability(this.selectedInstruments[0].instrumentSerial)
        .subscribe(async response => {
          let dialogRef;
          // Check if the instrument is in use in another experiment
          if (response && response.length > 0) {
            let message = response
              .map(
                (r: any) =>
                  `The Instrument ${r.InstrumentSerial} is used in LP exp ${r.ExperimentId}`,
              )
              .join('\n');
            message += `\n\nThe experiment(s) should be set to 'Analyzing Images' to free up the instruments and allow this experiment to be set as 'Ready'.`;
            dialogRef = this.dialogService.openConfirmDialog(message, {
              isOk: true,
              title: 'Instruments availability',
              isV2: true,
              width: '500px',
            });

            await dialogRef.afterClosed().toPromise();
            return; // Cancel the operation if the dialog is closed
          }

          this.dialogService
            .openConfirmDialog(
              `Are you sure to continue? If the Instrument Serial Number is replaced, the rest of the legend sequence will be asked in the new Instrument.`,
              {
                width: '400px',
                isOk: false,
              },
            )
            .afterClosed()
            .subscribe((result: any) => {
              if (result) {
                this.dialogRef.close(this.selectedInstruments);
              } else {
                this.notificationService.info('Replacement cancelled');
              }
            });
        }),
    );
  }

  isDisabled() {
    if (!this.selectedInstruments || this.selectedInstruments.length == 0) {
      return true;
    }
    if (this.selectedInstruments.length > 1) {
      return true;
    }
    if (this.selectedInstruments[0]?.instrumentSerial === this.currentInstrumentSerialNumber) {
      return true;
    }
    if (!this.selectedInstruments[0]?.hasLUTFile) {
      return true;
    }
    if (!this.selectedInstruments[0]?.hasFactoryCalibrationFile) {
      return true;
    }

    return false;
  }
}
